import React, { useContext, useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Link,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  formatAmount,
  getErrors,
  getErrorsFromArray,
  getBase64Promise,
  getMessage,
  getUserFullAddress,
  getUserFullName,
} from '../../util';
import logo from '../../assets/images/Goat-logo-emails.png';
import { hasBankAccount, hasPayPal } from '../../util/supplier/helper';
import { HttpContext } from '../../context/HttpContext';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AddPaymentDetails from '../account/PaymentDetailsForm/AddPaymentDetailsButton';
import InvoiceItemsInputs from './InvoiceItemsInputs';
import UploadFiles from '../campaigns/UploadCampaignFile';

function toCents(value) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100);
}

export default function InvoiceCreate({ order, supplierPo, me }) {
  const [supplier, setSupplier] = useState(supplierPo);
  const [itemsHasErrors, setItemsHasErrors] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(undefined);
  const [uploadFileError, setUploadFileError] = useState('');
  const [items, setItems] = useState(
    (order?.purchaseOrderItems || []).map((item) => ({
      id: item.id ?? null,
      clientId: item.id ? `po-${item.id}` : `tmp-${Date.now()}-${Math.random()}`,
      title: item.title || '',
      amount: item.amount ?? '',
      vat: !!item.vat,
    }))
  );
  function updateSupplier(supplier) {
    setSupplier(supplier);
  }
  const currencySymbol =
    (order && order.supplier.currency && order.supplier.currency.symbol) || null;
  const descriptionOfWork = (order && order.descriptionOfWork) || null;
  const number = (order && order.number) || null;
  const targetNetCents = toCents(order?.totalNet);
  const itemsNetCents = (items || []).reduce(
    (acc, item) => acc + toCents(item.amount),
    0
  );
  const isNetMismatch = targetNetCents !== itemsNetCents;

  const toast = useToast();
  const history = useHistory();
  const { authAxios } = useContext(HttpContext);

  async function getBase64(file) {
    return await getBase64Promise(file);
  }

  function updateUploadedFile(file) {
    setUploadedFile(file);
    setUploadFileError('');
  }

  const validationSchema = Yup.object().shape({
    referenceNumber: Yup.string().required('This value should not be blank.'),
  });

  const { register, handleSubmit, formState, setError } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  const { errors, isSubmitting, isValid } = formState;

  const onSubmit = async (values) => {
    try {
      const filteredItems = (items || []).map((item) => ({
        id: item.id ?? null,
        title: item.title,
        amount: item.amount.toString(),
        vat: !!item.vat,
      }));

      let submittedData = {
        ...values,
        purchaseOrder: order.id,
        purchaseOrderDataItems: { purchaseOrderItems: filteredItems },
        file: '',
      };

      if (uploadedFile) {
        const base64 = await getBase64(uploadedFile);
        const file = base64.split(',')[1];
        submittedData = {
          ...submittedData,
          file,
        };
      }

      const { data } = await authAxios.post(`/invoices`, submittedData);
      if (data) {
        history.push(`/orders/${order.id}`);
        toast({
          position: 'top',
          description:
            'You have successfully submitted an invoice. You will be notified when the Goat team approves it.',
          status: 'success',
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (onError) {
      console.log(onError);
      const errors = getErrors(onError);
      if (errors?.referenceNumber?.errors?.length) {
        const description = errors.referenceNumber.errors[0];
        setError('referenceNumber', {
          type: 'server',
          message: description,
        });
        toast({
          position: 'top',
          description,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (errors && errors.file && errors.file.errors) {
        const message = errors.file.errors[0];
        setUploadFileError(message);
        toast({
          position: 'top',
          description: message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      const globalErrors = getErrorsFromArray(onError);
      if (globalErrors?.length) {
        const description = globalErrors[0];
        toast({
          position: 'top',
          description,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });

        return;
      }

      const message = getMessage(onError);
      if (message) {
        toast({
          position: 'top',
          description: message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });

        if (
          message ===
          'You must set your VAT number before applying VAT on purchase order items.'
        ) {
          setTimeout(function () {
            history.push(
              `/account?redirect=create-invoice/${order.id}&edit=personal-information`
            );
          }, 3000);
        }
        if (onError?.code === 500) {
          setTimeout(function () {
            history.push('/orders');
          }, 3000);
        }
      }
    }
  };

  return (
    <>
      <Box
        bg="#fff"
        borderRadius="md"
        boxShadow="0 1px 13px rgba(29, 44, 45, 0.12);"
        mt="7"
        p="10"
        pt="8"
      >
        <HStack justifyContent="space-between" alignItems="center" pb="10px">
          <Box display="flex" alignItems="center">
            <img style={{ height: '76px', marginRight: '20px' }} src={logo} alt="" />

            <Box>
              <Text fontSize="40px" fontWeight="bold" mb="0">
                Invoice
              </Text>
              {/*<Text color=" #868789" fontSize="14px">*/}
              {/*  Invoice number will be auto generated*/}
              {/*</Text>*/}
            </Box>
          </Box>
          <form>
            <Box display="flex" alignItems="center">
              {' '}
              <FormControl isInvalid={errors.referenceNumber} isRequired mb={0}>
                <FormLabel htmlFor="invoiceEditHtmlFor">Reference number</FormLabel>
                <Input
                  isInvalid
                  name="referenceNumber"
                  id="referenceNumber"
                  focusBorderColor="black"
                  errorBorderColor="error.500"
                  placeholder="Type here"
                  {...register('referenceNumber')}
                />
                <FormErrorMessage>
                  {errors.referenceNumber?.message || errors.referenceNumber}
                </FormErrorMessage>
              </FormControl>
            </Box>
          </form>
        </HStack>
        <Box h="1px" width="100%" bg="#e8e9e9" mt="15px" mb="30px" />
        <HStack spacing={10} mb={7} alignItems="flex-start">
          <Box width="550px">
            <Text fontSize="20px" mb="10px" fontWeight="bold">
              To
            </Text>
            <HStack alignItems="flex-start">
              <Box fontSize="14px">
                <Text mb="5px">Goat Solutions Ltd</Text>
                <Text mb="5px">Sea Containers</Text>
                <Text mb="5px">18 Upper Ground, London, SE1 9GL, UK</Text>
                <Text mb="5px">VAT no: GB 512 5421 86</Text>
              </Box>
            </HStack>
          </Box>
          <Box>
            <Text fontSize="20px" mb="10px" fontWeight="bold">
              From
            </Text>
            <HStack alignItems="flex-start">
              <Box fontSize="14px" mr="35px">
                <Text mb="5px">{getUserFullName(me)}</Text>
                <Text mb="5px">{getUserFullAddress(me)}</Text>
                <Text mb="5px">
                  VAT no:
                  {me?.vatNumber ? (
                    <Text as="span"> {me?.vatNumber}</Text>
                  ) : (
                    <Text as="span" color="#868789">
                      {' '}
                      No VAT registered
                    </Text>
                  )}
                </Text>
                <Link
                  color="brand.500"
                  as={RouterLink}
                  to={`/account?redirect=create-invoice/${order.id}&edit=personal-information`}
                  fontSize="14px"
                  display="block"
                  mb="6px"
                >
                  Edit
                </Link>
              </Box>
            </HStack>
          </Box>
        </HStack>

        <HStack spacing={10} mb={14} alignItems="flex-start">
          <Box width="550px">
            <HStack alignItems="flex-start">
              <Box mt="30px">
                <Text fontSize="14px" fontWeight="700" mb="0px" color="#333">
                  Description of work
                </Text>
                <Text color="#333" fontSize="14px" maxWidth="100%">
                  {descriptionOfWork}
                </Text>
              </Box>
            </HStack>
          </Box>
          <Box>
            <HStack alignItems="flex-start">
              <Box mt="30px">
                <Text fontSize="14px" fontWeight="700" mb="0px" color="#333">
                  Invoice date
                </Text>
                <Text color="#868789" fontSize="14px" maxWidth="100%">
                  will be auto generated
                </Text>
              </Box>
            </HStack>
          </Box>
          <Box>
            <HStack alignItems="flex-start">
              <Box mt="30px">
                <Text fontSize="14px" fontWeight="700" mb="0px" color="#333">
                  Due date
                </Text>
                <Text color="#868789" fontSize="14px" maxWidth="100%">
                  will be auto generated
                </Text>
              </Box>
            </HStack>
          </Box>
          <Box>
            <HStack alignItems="flex-start">
              <Box mt="30px">
                <Text fontSize="14px" fontWeight="700" mb="0px" color="#333">
                  Purchase order number
                </Text>
                <Text color="#868789" fontSize="14px" maxWidth="100%">
                  {number ? number : <Box color="#a9b1b4">pending</Box>}
                </Text>
              </Box>
            </HStack>
          </Box>
        </HStack>

        <Box>
          <InvoiceItemsInputs
            items={items}
            currencySymbol={currencySymbol || ''}
            onItemsChange={setItems}
            onHasErrorsChange={setItemsHasErrors}
          />
          {isNetMismatch ? (
            <Box mt="10px">
              <Text color="error.500" fontSize="14px">
                Total net amount of items must equal the purchase order net amount (
                {currencySymbol}
                {formatAmount(order?.totalNet)}).
              </Text>
            </Box>
          ) : null}
        </Box>

        <Box mt="20px">
          <FormControl isInvalid={uploadFileError}>
            <FormLabel htmlFor="invoiceFile">Attach invoice file</FormLabel>
            <UploadFiles
              updateUploadedFile={updateUploadedFile}
              setUploadFileError={setUploadFileError}
            />
            <FormErrorMessage>{uploadFileError}</FormErrorMessage>
          </FormControl>
        </Box>

        <Box mt="30px">
          <Text fontSize="20px" mb="10px" fontWeight="bold">
            Payment details:
            {!supplier.paymentDetailsApproved && (
              <Text
                fontStyle="italic"
                fontSize="16px"
                color="#868789"
                fontWeight="400"
                display="inline-block"
                ml="3"
              >
                Processing
              </Text>
            )}
          </Text>
          <HStack alignItems="flex-start">
            {supplier && hasBankAccount(supplier) && (
              <>
                <Box color="#868789" fontSize="14px" mr="35px">
                  <Text mb="5px">Payment method:</Text>
                  <Text mb="5px">Bank name:</Text>
                  {supplier.bankAddress && <Text mb="5px">Bank address:</Text>}
                  <Text mb="5px">Country:</Text>
                  {supplier?.accountName && <Text mb="5px">Account name:</Text>}
                  {supplier.accountNumber && <Text mb="5px">Account number:</Text>}
                  {supplier.accountNumberOrIban && (
                    <Text mb="5px">Account number or IBAN:</Text>
                  )}
                  {supplier.bankCode && <Text mb="5px">Bank code:</Text>}
                  {supplier.bsbCode && <Text mb="5px">BSB code:</Text>}
                  {supplier.iban && <Text mb="5px">IBAN:</Text>}
                  {supplier.ifscCode && <Text mb="5px">IFSC code:</Text>}
                  {supplier.routingNumber && <Text mb="5px">Routing number:</Text>}
                  {supplier.sortCode && <Text mb="5px">Sort code:</Text>}
                  {supplier.swift && <Text mb="5px">SWIFT code:</Text>}
                  {supplier.additionalReference && (
                    <Text mb="5px">Additional reference:</Text>
                  )}
                  {/*<Link*/}
                  {/*  color="brand.500"*/}
                  {/*  as={RouterLink}*/}
                  {/*  to={`/account`}*/}
                  {/*  fontSize="14px"*/}
                  {/*  d="block"*/}
                  {/*  mb="6px"*/}
                  {/*>*/}
                  {/*  Edit*/}
                  {/*</Link>*/}
                  <Box marginLeft="-18px">
                    {supplier.paymentDetailsApproved && (
                      <AddPaymentDetails
                        edit
                        supplier={supplier}
                        updateSupplier={updateSupplier}
                      />
                    )}
                  </Box>
                </Box>
                <Box alignItems="flex-start" fontSize="14px">
                  <Text mb="5px">Bank transfer</Text>
                  <Text mb="5px">{supplier.bankName}</Text>
                  {supplier.bankAddress && <Text mb="5px">{supplier.bankAddress}</Text>}
                  <Text mb="5px">{supplier.country?.name}</Text>
                  {supplier?.accountName && <Text mb="5px">{supplier.accountName}</Text>}
                  {supplier.accountNumber && (
                    <Text mb="5px">{supplier.accountNumber}</Text>
                  )}
                  {supplier.accountNumberOrIban && (
                    <Text mb="5px">{supplier.accountNumberOrIban}</Text>
                  )}
                  {supplier.bankCode && <Text mb="5px">{supplier.bankCode}</Text>}
                  {supplier.bsbCode && <Text mb="5px">{supplier.bsbCode}</Text>}
                  {supplier.iban && <Text mb="5px">{supplier.iban}</Text>}
                  {supplier.ifscCode && <Text mb="5px">{supplier.ifscCode}</Text>}
                  {supplier.routingNumber && (
                    <Text mb="5px">{supplier.routingNumber}</Text>
                  )}
                  {supplier.sortCode && <Text mb="5px">{supplier.sortCode}</Text>}
                  {supplier.swift && <Text mb="5px">{supplier.swift}</Text>}
                  {supplier.additionalReference && (
                    <Text mb="5px">{supplier.additionalReference}</Text>
                  )}
                </Box>
              </>
            )}
            {supplier && hasPayPal(supplier) && (
              <>
                <Box color="#868789" fontSize="14px" mr="35px">
                  <Text mb="5px">Payment method:</Text>
                  <Text mb="5px">Email address:</Text>
                  {/*<Link*/}
                  {/*  color="brand.500"*/}
                  {/*  as={RouterLink}*/}
                  {/*  to={`/account`}*/}
                  {/*  fontSize="14px"*/}
                  {/*  d="block"*/}
                  {/*  mb="6px"*/}
                  {/*>*/}
                  {/*  Edit*/}
                  {/*</Link>*/}
                  <Box marginLeft="-18px">
                    {supplier.paymentDetailsApproved && (
                      <AddPaymentDetails
                        edit
                        supplier={supplier}
                        updateSupplier={updateSupplier}
                      />
                    )}
                  </Box>
                </Box>
                <Box alignItems="flex-start" fontSize="14px">
                  <Text mb="5px">PayPal</Text>
                  <Text mb="5px">{supplier.payPalAccount}</Text>
                </Box>
              </>
            )}
            {supplier && !hasPayPal(supplier) && !hasBankAccount(supplier) && (
              <>
                <Text color="#868789">No information yet.</Text>
              </>
            )}
          </HStack>
        </Box>
      </Box>
      <Box mt="20px" mb="30px" float="right">
        <Button
          type="submit"
          colorScheme="brand"
          size="lg"
          fontSize="16px"
          fontWeight="500"
          isFullWidth
          isLoading={isSubmitting}
          textTransform="initial"
          _hover={{ bg: '#000' }}
          disabled={
            !isValid ||
            isSubmitting ||
            itemsHasErrors ||
            isNetMismatch ||
            uploadedFile === undefined
          }
          onClick={handleSubmit(onSubmit)}
        >
          Send invoice
        </Button>
      </Box>
    </>
  );
}
